// Shared utilities for UpgradeList components
import { currencyFormat } from '../helpers.js';
import { iconMap } from '../components/iconMap.js';
import { ArrowUpCircleIcon } from "@heroicons/react/20/solid";
import { countryCodes, regionCodes, industries } from '../helperData.js';
import { meetsYearsInOperationRequirement, meetsDesignationRequirement } from './grantEligibility.js';
import { filterActiveGrants } from './grantArchive.js';
import { compareGrantsByPriority } from './grantPriority.js';

export { isGrantArchived, filterActiveGrants } from './grantArchive.js';

// Helper to parse currency/amount strings to numbers
export function parseAmount(val) {
	if (typeof val === 'number') return val;
	if (!val) return 0;
	// Remove $ and commas, then parse as float
	return parseFloat(String(val).replace(/[$,]/g, '')) || 0;
}

// Function to generate icon from domain
export function generateIconsFromTools(domain) {
	if (!domain || typeof domain !== 'string') return [];

	// Normalize the domain (remove protocol, www, etc.)
	const normalizedDomain = domain
		.toLowerCase()
		.replace(/^https?:\/\//, '') // Remove http:// or https://
		.replace(/^www\./, '') // Remove www.
		.replace(/\/$/, '') // Remove trailing slash
		.split('/')[0]; // Take only the domain part (before any path)

	// Generate icon URL
	const iconUrl = iconMap[normalizedDomain] || `https://img.logo.dev/${normalizedDomain}?token=pk_acnHOju_S3KawCcUE6Ek2Q`;

	return [iconUrl];
}

// Helper function to get grant name
export function getGrantName(grant) {
	const name = grant?.fields?.UpgradeName || grant?.fields?.Name || grant?.UpgradeName || grant?.Name || '';
	return name;
}

// Helper function to get grant status
export function getGrantStatus(grant) {
	const status = grant?.fields?.Status || grant?.Status || '';
	return status;
}

// Helper function to get grant date
export function getGrantDate(grant) {
	const candidates = [
		grant?.fields?.EstCompletionDate,
		grant?.fields?.Deadline,
		grant?.fields?.Date,
		grant?.EstCompletionDate,
		grant?.Deadline,
		grant?.Date
	];

	for (const val of candidates) {
		if (val == null || val === '') continue;
		let date;
		if (val instanceof Date) {
			date = val;
		} else if (typeof val === 'string') {
			const trimmed = val.trim();
			if (trimmed.length < 10) continue;
			date = new Date(trimmed.slice(0, 10));
		} else {
			date = new Date(val);
		}
		if (!isNaN(date.getTime())) return date;
	}
	return null;
}

// Add this helper function to find the earliest win date
export function getFirstWinDate(grants) {
	// Filter for awarded grants and get their completion dates
	const awardedGrants = grants.filter(grant => {
		const status = getGrantStatus(grant);
		return status === 'Awarded' || status === 'Cash Collected';
	});

	if (awardedGrants.length === 0) return null;

	// Get all possible completion dates
	const completionDates = awardedGrants
		.map(grant => getGrantDate(grant))
		.filter(date => date !== null);

	if (completionDates.length === 0) return null;

	// Find the earliest date
	const earliestDate = new Date(Math.min(...completionDates.map(d => d.getTime()))).toISOString().split('T')[0];
	return earliestDate;
}

// Helper function to get last awarded grant info
export function getLastAwardedGrant(grants) {
	const awardedGrants = grants.filter(grant => {
		const status = getGrantStatus(grant);
		return status === 'Awarded' || status === 'Cash Collected';
	});

	if (awardedGrants.length === 0) return { date: null, name: null };

	// Sort by date in descending order
	const sortedGrants = awardedGrants.sort((a, b) => {
		const dateA = getGrantDate(a);
		const dateB = getGrantDate(b);
		if (!dateA && !dateB) return 0;
		if (!dateA) return 1;
		if (!dateB) return -1;
		return dateB - dateA;
	});

	const lastGrant = sortedGrants[0];
	const result = {
		date: lastGrant ? getGrantDate(lastGrant)?.toISOString().split('T')[0] : null,
		name: lastGrant ? getGrantName(lastGrant) : null
	};

	return result;
}

// Add this helper function to find the next upcoming grant
export function getNextUpcomingGrant(grants) {
	const upcomingGrants = grants.filter(grant => {
		const status = getGrantStatus(grant);
		return status === 'Value Roadmap' || status === 'In Progress' || status === 'Submitted';
	});

	if (upcomingGrants.length === 0) return { date: null, name: null };

	// Sort by date in ascending order
	const sortedGrants = upcomingGrants.sort((a, b) => {
		const dateA = getGrantDate(a);
		const dateB = getGrantDate(b);
		if (!dateA && !dateB) return 0;
		if (!dateA) return 1;
		if (!dateB) return -1;
		return dateA - dateB;
	});

	const nextGrant = sortedGrants[0];
	const result = {
		date: nextGrant ? getGrantDate(nextGrant)?.toISOString().split('T')[0] : null,
		name: nextGrant ? getGrantName(nextGrant) : null
	};

	return result;
}

// Add this helper function before the UpgradesCompactMockup component
export function getEstCompletionDate(row) {
	// Try all possible fields, skipping empty strings
	const candidates = [
		row?.fields?.EstCompletionDate,
		row?.fields?.Deadline,
		row?.fields?.Date,
		row?.EstCompletionDate,
		row?.Deadline,
		row?.Date
	];
	for (const val of candidates) {
		if (val && typeof val === 'string' && val.length >= 10) return val.slice(0, 10);
	}
	// Default: today + 90 days
	const date = new Date();
	date.setDate(date.getDate() + 90);
	return date.toISOString().split('T')[0];
}

export function getLargestGrants(grants) {
	if (grants) {
		return [...grants]
			.sort((a, b) => {
				const priorityDiff = compareGrantsByPriority(a, b);
				if (priorityDiff !== 0) return priorityDiff;
				const aAmt = parseAmount(a.fields.Amount ?? a.fields.AnnualMargin);
				const bAmt = parseAmount(b.fields.Amount ?? b.fields.AnnualMargin);
				return bAmt - aAmt;
			})
			// Map to display string
			.map((g) => {
				const amt = parseAmount(g.fields.Amount ?? g.fields.AnnualMargin);
				return `${g.fields.Name} (${currencyFormat(amt)})`;
			})
			.slice(0, 3); // Top 3
	}
}

// Status options for grants
export const statusOptions = [
	{ value: 'Awarded', label: 'Awarded', color: 'bg-green-400' }, // Completed
	{ value: 'Cash Collected', label: 'Cash Collected', color: 'bg-green-400' }, // Completed
	{ value: 'Lost', label: 'Lost', color: 'bg-red-400' }, // Completed
	{ value: 'Applied - Not Heard Back', label: 'Applied - Not Heard Back', color: 'bg-blue-400' }, // On Deck (Next 90 Days)
	{ value: 'Applied - Heard Back', label: 'Applied - Heard Back', color: 'bg-blue-400' }, // On Deck (Next 90 Days)
	{ value: 'In Progress', label: 'In Progress', color: 'bg-blue-400' }, // On Deck (Next 90 Days)
	{ value: 'Value Roadmap', label: 'Value Roadmap', color: 'bg-yellow-300' }, // Value Roadmap (Next 12 Months)
	{ value: 'Interested', label: 'Interested', color: 'bg-yellow-300' }, // Value Roadmap (Next 12 Months)
	{ value: 'Paused', label: 'Paused', color: 'bg-red-300' }, // Paused or Cancelled
	{ value: 'Cancelled', label: 'Cancelled', color: 'bg-red-300' }, // Paused or Cancelled
	{ value: 'Not Eligible Based on Further Research', label: 'Not Eligible Based on Further Research', color: 'bg-red-300' }, // Paused or Cancelled
	{ value: 'Temporarily Closed', label: 'Temporarily Closed', color: 'bg-gray-300' }, // Temporarily Closed
	{ value: 'Client Has Already Applied For', label: 'Client Has Already Applied For', color: 'bg-purple-300' }, // Client Has Already Applied For
	{ value: 'Archived', label: 'Archived', color: 'bg-gray-300' }, // Archived
];

/**
 * Find grants that match company criteria
 * @param {string} country - Company country code(s) (e.g., "CA" or "CA,US")
 * @param {string} province - Company province/state code(s)
 * @param {string} locality - Company address/locality (e.g., "Toronto, Ontario")
 * @param {string|number} founded - Company founding year
 * @param {string} industry - Company industry (e.g., "Technology")
 * @param {string|number} employees - Number of employees
 * @param {string} profitable - Whether company is profitable ("Yes" or "No")
 * @param {Array} grants - Array of all grants to filter from
 * @param {string} typeOfMoney - Type of money to filter (default: 'both')
 * @param {string} companyType - CompanyType, used for the Non-profit designation gate
 * @param {boolean} indigenousTeamMember - IndigenousTeamMember, used for the Indigenous designation gate
 * @returns {Array} Filtered grants matching company criteria
 */
export function find_grants(
  country,
  province,
  locality,
  founded,
  industry,
  employees,
  profitable,
  grants,
  typeOfMoney = 'both',
  companyType = '',
  indigenousTeamMember = false,
  womenLed = false
) {
  if (!grants || grants.length === 0) {
    return [];
  }

  grants = filterActiveGrants(grants);

  if (grants.length === 0) {
    return typeOfMoney === 'sum' ? 0 : [];
  }

  var country = country || '';
  var province = province || '';
  var founded = founded || '';
  var industry = industry || '';
  var locality = locality || '';
  var employees = employees || '';
  var profitable = profitable || '';
  var typeOfMoney = typeOfMoney || '';

  var data = grants;
  var sheetLength = grants?.length;
  var sum = 0;
  var new_array = [];

  const countryNames = Array.isArray(Object.values(countryCodes || {})) ? Object.values(countryCodes || {}) : [];
  // Support both codes (CA) and full names (Canada)
  const countryOfCompany = (country || '')
    .trim()
    .split(',')
    .map((c) => c.trim())
    .flatMap((c) => {
      const upper = c.toUpperCase();
      const mapped = countryCodes[upper];
      return mapped ? [mapped] : [c.toLowerCase()];
    })
    .filter(Boolean);

  // Province from ProvinceOrState only (not city/locality) - support both codes (ON) and full names (Ontario)
  const allRegionNames = [regionCodes.CA, regionCodes.US]
    .filter(Boolean)
    .flatMap((r) => Object.values(r));
  let provincesOfCompany = (province || '')
    .split(',')
    .map((item) => item.trim())
    .flatMap((code) => {
      const upper = code.toUpperCase();
      for (const countryCode of Object.keys(regionCodes)) {
        if (Object.keys(regionCodes[countryCode]).includes(upper)) {
          return [regionCodes[countryCode][upper]];
        }
      }
      // Full name? (e.g. "Ontario")
      const lower = code.toLowerCase();
      return allRegionNames.includes(lower) ? [lower] : [];
    })
    .filter(Boolean);

  const industryStr = (industry && typeof industry === 'string') ? industry : '';
  var industry = industryStr.trim().toLowerCase().split(',').map((element) => element.trim()).filter(Boolean);

  for (var i = 0; i < sheetLength; i++) {
    const eligibleIndustry = data[i]?.fields?.Eligible_Industry;
    var grantIndustry = (eligibleIndustry && typeof eligibleIndustry === 'string')
      ? eligibleIndustry.toLowerCase().split(',').map((element) => element.trim()).filter(Boolean)
      : [];

    function checkIndustry(specificIndustry) {
      // check to see if the company's industry includes "technology"
      if (grantIndustry[0] === industry[0] || grantIndustry[0] === 'all') {
        return true;
      } else {
        const industryList = Array.isArray(industries?.[specificIndustry]) ? industries[specificIndustry] : [];
        const grantArr = Array.isArray(grantIndustry) ? grantIndustry : [];
        const companyArr = Array.isArray(industry) ? industry : [];
        const safeIndustryList = (industryList ?? []);
        return (
          (safeIndustryList ?? []).some((item) => (grantArr ?? []).includes(item)) &&
          (safeIndustryList ?? []).some((item) => (companyArr ?? []).includes(item))
        );
      }
    }

    const industryKeys = (industries && typeof industries === 'object' ? Object.keys(industries) : []) || [];
    const safeIndustryKeys = Array.isArray(industryKeys) ? industryKeys : [];
    var industryCheck = industry?.length && industry[0] ? safeIndustryKeys.some(checkIndustry) : true;

    // CHECK 2 = required years in operation (Eligible_Founded; 0 = no requirement)
    var foundedCheck = meetsYearsInOperationRequirement(founded, data[i]?.fields?.Eligible_Founded);

    // CHECK 3 = location: country and province only (no city)
    const rawRegion = data[i]?.fields?.Eligible_Region;
    const grantRegionStr = typeof rawRegion === 'string' ? rawRegion.trim() : '';
    const grantRegions = Array.isArray(rawRegion)
      ? rawRegion.map((r) => String(r || '').trim().toLowerCase()).filter(Boolean)
      : (grantRegionStr
          ? grantRegionStr.split(',').map((r) => r.trim().toLowerCase()).filter(Boolean)
          : []);

    // Skip location filter if company has no country/province, or grant has no region (assume nationwide)
    const safeGrantRegions = Array.isArray(grantRegions) ? grantRegions : [];
    const safeCountryNames = Array.isArray(countryNames) ? countryNames : [];
    const safeCountryOfCompany = Array.isArray(countryOfCompany) ? countryOfCompany : [];
    const safeProvincesOfCompany = Array.isArray(provincesOfCompany) ? provincesOfCompany : [];
    let locationCheck =
      (!safeCountryOfCompany.length && !safeProvincesOfCompany.length) || !safeGrantRegions.length;

    if (!locationCheck && safeGrantRegions.length > 0) {
      // Match by country (e.g. grant "Canada" vs company Country "CA")
      locationCheck = safeGrantRegions.some(
        (r) => safeCountryNames.includes(r) && safeCountryOfCompany.includes(r)
      );
      // Or match by province (e.g. grant "Ontario" vs company ProvinceOrState "ON")
      if (!locationCheck) {
        locationCheck = safeGrantRegions.some((r) => safeProvincesOfCompany.includes(r));
      }
    }

    // CHECK 4 = does the company have enough employees (skip if company employees missing)
    const companyEmployees = parseFloat(employees);
    var employeesCheck = isNaN(companyEmployees) || !data[i]?.fields?.Eligible_Employees
      ? true
      : parseFloat(data[i].fields.Eligible_Employees) <= companyEmployees;

    // CHECK 5 = does the company have enough revenue (skip if company employees missing)
    var revenueCheck = isNaN(companyEmployees) || !data[i]?.fields?.Eligible_Revenue
      ? true
      : parseFloat(data[i].fields.Eligible_Revenue) <= companyEmployees * 100000;

    // CHECK 6 = is the grant a favorite, approved by Upgraded
    var favoriteCheck = true;

    // CHECK 7 = are you looking for grants, loan or both
    var typeCheck = true;

    // CHECK 8 = does the grant require that the company is profitable?
    var profitabilityCheck =
      data[i]?.fields?.Eligible_Profitable === 'No'
        ? true
        : profitable === 'Yes'
          ? true
          : false;

    // Check 9 : Open or Closed Check. Is the grant or loan currently open?
    var openOrClosedCheck = data[i]?.fields?.Deadline !== "Closed";

    // TimeEstimateToComplete: pass if missing/null (treat as "unknown, don't exclude")
    const timeEst = data[i]?.fields?.TimeEstimateToComplete;
    var timeCheck = timeEst == null || timeEst === '' || parseFloat(timeEst) <= 90;

    var designationCheck = meetsDesignationRequirement(
      { companyType, indigenousTeamMember, womenLed },
      data[i]?.fields
    );

    const conditionsArray = [
      industryCheck,
      foundedCheck,
      locationCheck,
      employeesCheck,
      revenueCheck,
      favoriteCheck,
      typeCheck,
      profitabilityCheck,
      openOrClosedCheck,
      timeCheck,
      designationCheck
    ];

    if (!conditionsArray.includes(false)) {
      // Return original grant object with computed fields added
      const grant = data[i];
      const pSuccess = parseFloat(grant.fields?.pSuccess) ?? 0.65;
      const amount = parseAmount(grant.fields?.Amount);
      
      // Add computed fields to the grant object (don't transform, just add what's needed)
      grant.fields = {
        ...grant.fields,
        // Computed fields for compatibility
        AnnualMargin: amount,
        ExpectedValue: amount * pSuccess,
        // Keep id as recordId for reference, but add index-based id for grantWriters compatibility
        id: grant.id || grant.recordId || i,
      };
      
      new_array.push(grant);
    }
  }

  if (typeOfMoney === 'sum') {
    sum = new_array.reduce(
      (partialSum, grant) => {
        const expectedValue = grant.fields?.ExpectedValue || 
          parseAmount(grant.fields?.Amount) * (parseFloat(grant.fields?.pSuccess) ?? 0.65);
        return partialSum + expectedValue;
      },
      0
    );
    return sum;
  } else {
    return new_array;
  }
}
